/**
 * NFR-9 — the one place configuration lives.
 *
 * Every default, threshold and cap in Touchline is declared here and validated at import time by
 * `./validate.ts`. There are no magic numbers at call sites. Adding a number to a call site is a
 * review failure, not a style preference: the four gesture thresholds and `REORDER_QUIET_MS` are
 * expected to move after first field use (VER-3 item 4c), and they can only move as a config edit
 * if they never leaked.
 *
 * Section 1 is the complete design-spec §1.3 inventory, reproduced including the values the design
 * set. It is present in full even though build units U-0 and U-1 use only a fraction of it —
 * NFR-9 says config lives in one module, and splitting it "when the rest is built" is how the
 * discipline erodes.
 *
 * Section 2 is constants U-0 introduces, section 3 constants U-1 introduces. Each names its source
 * requirement. See the note on `LOG_RING_CAP` — §1.3 claims to be complete and is not.
 *
 * BUILD-UNIT NOTE (U-2): design §1.3 is authoritative for **names** as well as values. Every name
 * here now matches it, and `spec13.test.ts` diffs the two in both directions on every test run —
 * a §1.3 row with no constant, and a constant whose value disagrees, both fail the build. That
 * check is what §1.3 itself says is the only thing that closes the gap; it is no longer a comment.
 *
 * BUILD-UNIT NOTE (U-1): section 1 previously stopped at `SWIPE_MIN_PX` while claiming to be the
 * complete §1.3 set — sixteen declared rows were missing, including `PLAYABILITY_PROBE_TIMEOUT_MS`,
 * which U-1 needs. A completeness claim is what makes a later reader stop looking, so the claim has
 * to be earned; the missing rows are added below. Reported to the orchestrator.
 */

// ─────────────────────────────────────────────────────────────────────────────
// 1. Design spec §1.3 — the complete v1 inventory
// ─────────────────────────────────────────────────────────────────────────────

/** NFR-9 — count at which a hypothesis auto-confirms (INV-3). */
export const PATTERN_THRESHOLD = 3;

/** NFR-9 — maximum hypotheses holding a `boardPosition` per match (INV-5). */
export const BOARD_CAP = 8;

/** NFR-9 — required count of `phase=fullTime` TalkPoints on an archived match (INV-6). */
export const TALKPOINT_COUNT = 3;

/** NFR-9 — voice-note auto-stop (AUD-2), seconds. */
export const VOICE_NOTE_MAX_S = 60;

/** NFR-9 — how long a `−1` affordance stays live after an increment (LIVE-6), seconds. */
export const UNDO_WINDOW_S = 8;

/** NFR-9 — match minute from which the "Lock My 8" affordance appears (LOCK-1). User-configurable per BAK-5. */
export const LOCK_NUDGE_MIN = 15;

/** NFR-9 — minutes past `halfLengthMin` before the "end the half?" nudge (SES-7). */
export const HALF_OVERRUN_NUDGE_MIN = 10;

/** NFR-9, value set by design §5.5 / §15 (DD-1) — no row may change rendered position within this window of any tap (DISC-7). */
export const REORDER_QUIET_MS = 1000;

/** NFR-9, value set by design §2.2 (SES-1) — sustained press required by the four clock-anchoring transitions. */
export const TRANSITION_HOLD_MS = 700;

/** SES-5 — default half length in minutes. */
export const HALF_LENGTH_DEFAULT = 35;

/** SES-5 — inclusive lower bound on `Match.halfLengthMin`. */
export const HALF_LENGTH_MIN_MIN = 20;

/** SES-5 — inclusive upper bound on `Match.halfLengthMin`. */
export const HALF_LENGTH_MIN_MAX = 45;

/**
 * Design §1.3 / §5.3 — a **logged measurement threshold**, never a UI branch.
 *
 * This is the number build unit U-0 exists to test the design's tap path against. A commit slower
 * than this is not an error and produces no user-visible state; it is counted into the p95 that
 * §5.1 writes into the match archive.
 */
export const ACK_COMMIT_BUDGET_MS = 60;

/** Design §1.3 / §8.5 — `MediaRecorder` chunk interval; bounds worst-case audio loss on hard jetsam. */
export const TIMESLICE_MS = 1000;

/** Design §1.3 / §7.3 — bounds the HT-5 halftime adjustment candidate list. */
export const HT_ADJUSTMENT_CANDIDATES = 8;

/** Design §1.3 / §5.5 (DD-1) — press duration that classifies as a long-press. */
export const LONG_PRESS_MS = 450;

/** Design §1.3 / §5.5 (DD-1) — movement above this cancels a tap or long-press classification. */
export const TAP_SLOP_PX = 10;

/** Design §1.3 / §5.5 (DD-1) — minimum travel that classifies as a swipe. */
export const SWIPE_MIN_PX = 44;

/** NFR-2 — cold launch to interactive, milliseconds. */
export const COLD_LAUNCH_BUDGET_MS = 2000;

/**
 * DISC-3, and the targets that name 60 on their own authority — points.
 *
 * NFR-3's *"all in-play touch targets ≥ 60 pt equivalent"* was struck in requirements revision 4,
 * and NFR-10's quantified floors were struck in revision 5 (design §6.4b, D-32). The constant
 * remains because DISC-3 and the tile/row sizing genuinely use it, not because a general floor
 * binds. Sizing on a capture surface is by role and element weighting — the revision-5 rule — and
 * no uniform floor is cited to size anything.
 */
export const MIN_TOUCH_TARGET_PT = 60;

/** LIVE-1 — minimum Flag bar height, points. */
export const FLAG_BAR_MIN_PT = 80;

/** NFR-4 — VER-3 checklist threshold: storage after a three-game day, megabytes. */
export const STORAGE_BUDGET_3MATCH_MB = 100;

/** PRE-2 — minimum seeded hypotheses per moment bucket; enforced by the CI seed validation. */
export const MIN_SEEDS_PER_BUCKET = 10;

/** OBS-2 — taps on the version string that open the debug screen. */
export const DEBUG_TAP_COUNT = 5;

/** LIVE-2 — the count flash scale factor ("~2×"). */
export const ACK_FLASH_SCALE = 2;

/**
 * NFR-1 — tap-to-visual-ack budget in milliseconds, measured on the target device.
 *
 * The assertion threshold for build unit U-0 and for VER-1. A run's verdict is stated against this,
 * and against A-1's tighter ~40 ms p95 claim (`A1_ASSUMED_WARM_P95_MS`).
 */
export const TAP_ACK_BUDGET_MS = 100;

/** LIVE-2 — the acknowledgment persists at least this long once begun. */
export const ACK_PERSIST_MIN_MS = 300;

/** Design §1.3 / §2.2 — tolerance on `anchorTs` validation, covering dispatch and event-loop latency. */
export const HOLD_SLOP_MS = 250;

/** Design §1.3 / §5.5 — FLIP settle duration for the DISC-7 deferred reorder. */
export const REORDER_ANIM_MS = 200;

/** Design §1.3 / §6.4 — in-tile `−1` hit region inset, points. */
export const UNDO_INSET_PT = 44;

/** Design §1.3 / §7.3 — minimum type size for the read-aloud HT-5 adjustment list, points. */
export const ADJUSTMENT_TEXT_PT = 28;

/** Design §1.3 / §11.2 — manifest `GET` timeout. */
export const MANIFEST_TIMEOUT_MS = 30_000;

/** Design §1.3 / §11.2 — archive `PUT` timeout. */
export const ARCHIVE_PUT_TIMEOUT_MS = 120_000;

/** Design §1.3 / §15 DD-4 — the backup service's 413 threshold, megabytes. */
export const MAX_ARCHIVE_MB = 64;

/**
 * Design §1.3 / §8.2 — how long the playability probe waits before treating a segment as unplayable.
 *
 * §13.1 pairs it with "no retry — treat as unplayable". Build unit U-1 is the first consumer: it is
 * the bound on the `HTMLAudioElement` probe that decides whether a concatenated chunk sequence is
 * playable at all, which is the crux of assumption A-2.
 */
export const PLAYABILITY_PROBE_TIMEOUT_MS = 3000;

/**
 * OBS-1 — "a capped ring buffer of ~2,000 entries in IndexedDB in production".
 *
 * Absent from the first version of design §1.3 and found by build unit U-0, which reported it; §1.3
 * now carries the row and credits the finding. Left uncorrected, the first logger implementation
 * writes `2000` at a call site, which is exactly what NFR-9 forbids.
 */
export const LOG_RING_CAP = 2000;

/** Design §1.3 / §12.1 — ring trimming is amortized: trim on every Nth flush, not every flush. */
export const LOG_TRIM_EVERY_N_FLUSHES = 256;

/**
 * Design §1.3 / §13 — warn when `storage.estimate()` usage crosses this fraction of quota (BAK-3,
 * NFR-4).
 *
 * A fraction, not a percent. The unit is in the identifier because the two forms differ by 100x and
 * nothing at the call site would catch it.
 */
export const STORAGE_WARN_FRACTION = 0.8;

// ─────────────────────────────────────────────────────────────────────────────
// 2. Introduced by build unit U-0
// ─────────────────────────────────────────────────────────────────────────────

/** OBS-1 / design §12.1 — flush the in-memory log buffer once it reaches this many entries. */
export const LOG_FLUSH_THRESHOLD = 32;

/** Design §0.1 A-1 — the p95 the design assumes for a warm two-row `readwrite`. */
export const A1_ASSUMED_WARM_P95_MS = 40;

/** U-0 — trials discarded before measurement begins, so JIT warm-up is not counted as a commit cost. */
export const HARNESS_WARMUP_TRIALS = 20;

/** U-0 — default measured trials per condition. Large enough that a p99 means something. */
export const HARNESS_TRIALS_DEFAULT = 200;

/** U-0 — lower bound on measured trials. Below this a p99 is noise and the verdict says so. */
export const HARNESS_TRIALS_MIN = 50;

/** U-0 — upper bound on measured trials, so a mistyped value cannot lock the phone up at a venue. */
export const HARNESS_TRIALS_MAX = 2000;

/** U-0 — sample count below which a p99 is reported but explicitly not trusted. */
export const HARNESS_P99_MIN_SAMPLES = 100;

/**
 * U-0 — hard cap on a live-tap session, so a pad left face-up in a pocket cannot grow a run without
 * bound. It is a safety stop, **not a target**: the pad is open-ended and the operator decides when
 * to finish.
 *
 * There is deliberately no separate "live-tap target" constant. A first device run shipped one — a
 * literal `30` in the harness screen — while admissibility is gated on `HARNESS_TRIALS_MIN = 50`,
 * so the live-tap mode was structurally incapable of ever returning anything but `inconclusive`,
 * on the one measurement that carries real touch input. Two independent numbers that must agree
 * will eventually disagree. The pad now reads the floor itself, and the only remaining bound is
 * this ceiling, which `validate.ts` refuses to let fall below the floor.
 */
export const HARNESS_LIVE_TAP_MAX = 500;

/**
 * U-0 — how long to wait for an `IDBTransaction` `complete` event before calling a trial failed.
 *
 * A stall detector, not a threshold: two orders of magnitude above NFR-1's budget, so anything
 * approaching it is already a catastrophic result the report will show on its own. It exists so a
 * transaction that never commits becomes a recorded failure rather than a harness that hangs at a
 * venue with a progress bar that never moves.
 */
export const HARNESS_COMMIT_EVENT_TIMEOUT_MS = 5000;

/**
 * U-0 — same role for the animation frame. iOS stops delivering frames to a backgrounded page, so
 * without this bound, switching apps mid-run would leave the harness waiting forever.
 */
export const HARNESS_FRAME_TIMEOUT_MS = 5000;

/**
 * U-0 — gap between trials in "realistic" pacing mode, milliseconds.
 *
 * Two pacings are measured because they stress different things. Burst pacing (frame-paced, ~16 ms
 * apart) keeps the IndexedDB connection hot and exercises design §5.2's single-flight queue.
 * Realistic pacing lets the connection idle between taps, which is what actually happens on the
 * Discovery pages, and is where a re-warm cost would show up.
 */
export const HARNESS_PACING_REALISTIC_MS = 250;

/** U-0 — default thermal soak duration before the loaded condition measures, in seconds. */
export const HARNESS_THERMAL_SOAK_S = 120;

/** U-0 — cap on soak duration, so the harness cannot be told to cook the phone indefinitely. */
export const HARNESS_THERMAL_SOAK_MAX_S = 900;

/** U-0 — heat workers spawned for the thermally-loaded condition, capped regardless of core count. */
export const HARNESS_HEAT_WORKERS_MAX = 6;

/** A-6 — matches a season holds; the default seed size models a mid-season database. */
export const SEED_MATCHES_DEFAULT = 20;

/** A-6 — "a match produces 100–600 increments"; the midpoint is the default per-match seed. */
export const SEED_OBSERVATIONS_PER_MATCH_DEFAULT = 400;

/** A-6 — hypotheses per seeded match: `BOARD_CAP` positioned plus a realistic list-only tail. */
export const SEED_HYPOTHESES_PER_MATCH = 28;

/** U-0 — rows written per seeding transaction, so a large seed does not hold one transaction open for minutes. */
export const SEED_CHUNK_ROWS = 500;

/** U-0 — size of one synthetic audio blob when bulk-seeding storage, in bytes (≈120 KB, a ~10 s AAC segment). */
export const SEED_AUDIO_BLOB_BYTES = 120_000;

/** U-0 — retained run records; a run is a few KB, and a venue session produces a handful. */
export const HARNESS_RUN_HISTORY_CAP = 50;

// ─────────────────────────────────────────────────────────────────────────────
// 3. Introduced by build unit U-1 — the MediaRecorder spike (assumption A-2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AUD-1 — the MIME types the capability probe asks `MediaRecorder.isTypeSupported` about.
 *
 * Data, not logic: AUD-1 forbids transcoding on any path, so the product must store whatever the
 * recorder emits. This list exists to find out **what that is**, not to pick a favourite. iOS
 * Safari is expected to answer only for the `audio/mp4` family and Chromium only for the WebM/Opus
 * family — which is precisely why a desktop run cannot settle A-2.
 */
export const AUDIO_MIME_CANDIDATES: readonly string[] = [
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/aac',
  'audio/webm',
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/mpeg',
  'audio/wav',
];

/**
 * U-1 — default length of one spike segment, seconds.
 *
 * Long enough that `AUDIO_SPIKE_MIN_CHUNKS` timeslices arrive with margin, so the sequence being
 * concatenated is a real sequence rather than one chunk plus a stub. Short enough that several
 * segments fit inside a halftime break.
 */
export const AUDIO_SPIKE_SEGMENT_S = 12;

/** U-1 — floor on a spike segment. Below this the chunk sequence is too short to say anything. */
export const AUDIO_SPIKE_SEGMENT_MIN_S = 4;

/**
 * U-1 — chunks below which a segment cannot settle A-2.
 *
 * A-2 claims "a timesliced chunk sequence concatenated verbatim (init fragment first, chunks in
 * order) is playable". A two-chunk segment cannot distinguish "concatenation works" from "the
 * recorder happened to emit one usable file", so such a segment is reported and excluded from the
 * verdict rather than counted as a pass.
 */
export const AUDIO_SPIKE_MIN_CHUNKS = 4;

/**
 * U-1 — complete segments required before the A-2 verdict may read anything but `inconclusive`.
 *
 * `empirical-absence-not-invariant`: one playable assembly is an anecdote. Three independent
 * segments is still small, but it is the difference between "it worked" and "it works".
 */
export const AUDIO_SPIKE_MIN_SEGMENTS = 3;

/** U-1 — `getUserMedia` acquisition trials per pass. */
export const GUM_TRIALS_DEFAULT = 5;

/** U-1 — lower bound on acquisition trials; below this the spread is not a spread. */
export const GUM_TRIALS_MIN = 3;

/** U-1 — upper bound, so a mistyped value cannot cycle the microphone a hundred times at a venue. */
export const GUM_TRIALS_MAX = 25;

/**
 * Design §8.1 — the upper end of the design's own stated cost for `getUserMedia` on iOS
 * ("200–800 ms"), and therefore the threshold that falsifies it.
 *
 * §8.1 rejects per-recording acquisition on exactly this number, so it is a claim U-1 must check
 * rather than inherit. The *warm* path is checked against `TAP_ACK_BUDGET_MS` instead — §8.1 says
 * acquisition "costs nothing" once the stream is held, and FLAG-1's "starts recording immediately"
 * is the same perceptual promise NFR-1 already quantifies.
 */
export const GUM_COLD_ASSUMED_MAX_MS = 800;

/**
 * U-1 — tolerance on the observed timeslice cadence, as a fraction of `TIMESLICE_MS`.
 *
 * The design depends on the cadence only through §8.5's "worst-case loss is one timeslice", so what
 * has to hold is that the interval is *bounded and close to what was asked for*, not that it is
 * exact. A median inside ±50 % keeps §8.5's claim honest.
 */
export const TIMESLICE_TOLERANCE_FRACTION = 0.5;

/**
 * U-1 — multiple of `TIMESLICE_MS` the **worst** observed inter-chunk interval may reach.
 *
 * If a single gap runs to twice the timeslice, §8.5's "at most one `TIMESLICE_MS` lost" understates
 * the loss on a hard jetsam kill and the sentence needs correcting.
 */
export const TIMESLICE_MAX_INTERVAL_FACTOR = 2;

/** U-1 — retained spike runs. A run carries stats, not audio, so it is a few KB. */
export const AUDIO_RUN_HISTORY_CAP = 25;

/**
 * U-1 — the pretend match minute a spike segment is stamped with by default.
 *
 * 44′ is the boundary case design §8.2 names by name: *"a flag started at 44:50 and stopped after
 * the Half tap"*. The spike has no state machine, so this is the value that demonstrates SES-10's
 * ordering — the minute is derived when recording STARTS, not when it finalizes.
 */
export const AUDIO_SPIKE_DEFAULT_MATCH_MINUTE = 44;

/**
 * U-1 — upper bound on the pretend match minute input.
 *
 * Derived rather than picked: the largest minute the product's own clock can produce is two
 * maximum-length halves plus the overrun the SES-7 nudge tolerates.
 */
export const AUDIO_SPIKE_MAX_MATCH_MINUTE = HALF_LENGTH_MIN_MAX * 2 + HALF_OVERRUN_NUDGE_MIN;

/**
 * U-1 — cap on persisted spike segments before the oldest are swept.
 *
 * These *do* carry audio, and a venue session should not silently fill the phone. The sweep is
 * oldest-first and only ever touches segments the spike itself created.
 */
export const AUDIO_SEGMENT_HISTORY_CAP = 12;

// ─────────────────────────────────────────────────────────────────────────────
// 4. Identifiers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The harness writes to its OWN IndexedDB database, not the product's.
 *
 * Two reasons, both load-bearing. (1) The harness seeds and wipes thousands of rows; doing that to
 * the database that will later hold real scouting data is a data-loss path with no upside.
 * (2) U-3 owns the product's `v001` schema, and a harness that had already claimed a Dexie version
 * number on the product database would force U-3 to start at `v002` for no reason.
 */
export const HARNESS_DB_NAME = 'touchline-u0-harness';

/**
 * Forward-only numbered migrations from v001 (NFR-7). This is the harness DB's current version.
 *
 * v002 (build unit U-1) adds the audio-spike stores. v001 is never edited — a device that already
 * applied it will never apply it again, so an edit would produce two different v001 schemas in the
 * field.
 */
export const HARNESS_DB_VERSION = 2;

/**
 * The **product** database. Separate from the harness DB, and starting its own numbering at v001.
 *
 * NFR-7 requires "numbered forward-only Dexie migrations from v001" and a fixture DB from the first
 * shipped schema that survives every later migration. The harness already burned v001 and v002 on
 * `touchline-u0-harness`; extending that chain would mean the product's first shipped schema was
 * `v003` and the NFR-7 fixture would be a harness artifact. Its own database is what keeps the
 * product's migration story the one NFR-7 describes.
 */
export const PRODUCT_DB_NAME = 'touchline';

/**
 * The product schema's current Dexie version (NFR-7). Kept in step with
 * `data/migrations/product/index.ts`, which the schema test asserts in both directions.
 *
 * `5` since `v005_team_profile.ts` (B spec §15.5) — the team profile's **clean start**: it clears
 * `ourPlayers`, changes no store and no index, and **is not recoverable in-app**. Read its file
 * comment before deploying; §25's operational note is a pre-condition, not advice.
 * `4` was `v004_b_data_model.ts` (B spec §4.1) adding `Match.purpose` plus the three B tables
 * (`ourPlayers`, `annotations`, `coachCards`); `3` was `v003_match_format.ts`'s `Match.format`
 * (design §6.0a/§13.3, PRE-8); `2` was `v002_hypothesis_created_at.ts`'s `Hypothesis.createdAt`
 * (§3.2). This is not a tunable — it is the pin `db.test.ts` diffs against
 * `LATEST_PRODUCT_VERSION`, which is derived from the chain's length, so appending a migration
 * file without moving this number fails a test rather than shipping a database that never upgrades.
 */
export const PRODUCT_DB_VERSION = 5;

// ─────────────────────────────────────────────────────────────────────────────
// 5. Introduced by build unit U-3 — schema, repos, BlobStore
// ─────────────────────────────────────────────────────────────────────────────

/**
 * U-3 / design §5.1 step 8 — how long a write waits for the native `IDBTransaction` `complete`
 * event before it is declared uncommittable.
 *
 * A **stall detector, not a threshold**: two orders of magnitude above NFR-1's `TAP_ACK_BUDGET_MS`,
 * so anything approaching it is already a catastrophic result. It exists because SES-2 forbids
 * acknowledging an uncommitted mutation — so a transaction that never reports `complete` must
 * become a surfaced, typed failure rather than a screen that never flashes and never explains why.
 */
export const COMMIT_EVENT_TIMEOUT_MS = 5000;

/**
 * U-3 / §11.7 — decimal places geometry is rounded to before it is stored.
 *
 * §11.7's determinism controls require "no floats in serialized geometry beyond 4 dp", because
 * float formatting varies between engines and one differing digit defeats INV-12's byte-identity.
 * Rounding at the **repo boundary**, not in the serializer, is what makes that achievable: U-5 can
 * only be byte-deterministic if the bytes it is handed already are. SP-5's markers are normalized
 * 0–1 box coordinates, so 4 dp is ~0.1 mm on a penalty-box diagram — far below what a fingertip
 * can express.
 */
export const GEOMETRY_DECIMALS = 4;

// ─────────────────────────────────────────────────────────────────────────────
// 6. Introduced by build unit U-5 — the archive format
// ─────────────────────────────────────────────────────────────────────────────

/**
 * U-5 / §11.7 — the `formatVersion` written into every `manifest.json`.
 *
 * The **only** version negotiation SYN-9 allows: *"A client-side schema migration MUST NOT require a
 * coordinated server deploy. Any archive the service holds remains retrievable and importable by a
 * later client version; version negotiation, where needed, happens client-side on import."* SYN-8
 * forbids the service from parsing the archive at all, so the version must travel inside the bytes
 * the service treats as opaque, and the importer must read it before anything else.
 *
 * Distinct from `PRODUCT_DB_VERSION`, and deliberately so: the Dexie version describes the shape of
 * the database on *this* device, the format version describes the shape of a file that outlives it.
 * ~~A migration that adds a nullable column changes the first and not the second.~~ **That was
 * wrong, and U-23 is where it cost something.** What decides this number is not the column's
 * nullability but whether the field is *published*: `Match.format` is in `MATCH_FIELDS`, so archives
 * written before it existed are missing a field the current `MatchSchema` requires, and
 * `validateImport` would quarantine every such match **together with its hypotheses, observations
 * and audio**. The Dexie migration does not reach a file (§11.7a).
 *
 * **2 — `Match.format` (PRE-8, U-23, §11.7a).** Read together with `archive/import.ts`'s
 * `MATCH_FORMAT_ARCHIVE_VERSION`, which is where an older archive's missing `format` is backfilled
 * before validation. An older client meeting a `formatVersion: 2` archive refuses it with SYN-9's
 * worded message rather than importing a match with no format — the negotiation this field exists
 * for, working as designed.
 *
 * **3 — the B data model (B spec §4.1/§4.4, migration `v004`).** `Match.purpose` joins
 * `MATCH_FIELDS` (a published field the current `MatchSchema` requires — the same reasoning that
 * made `format` a bump), and three new entities join the format: `annotations`, `coachCards`
 * (match-scoped) and `ourPlayers` (profile-scoped). Read together with `archive/import.ts`'s
 * `MATCH_PURPOSE_ARCHIVE_VERSION`: an older archive's missing `purpose` is backfilled
 * `'teamMatch'` (F-2's ruling) before validation, and the new entities arrive empty — **an A
 * export therefore seeds B unchanged** (scenario 35). A (frozen, reading 2) refuses a B export
 * via SYN-9's one-directional gate, which is correct — A is insurance, not a destination.
 *
 * **4 — the team profile (§3.7.36–39, B spec §15.6, migration `v005`).** `OUR_PLAYER_FIELDS`
 * changes **completely** — `label` and `attributes` out, `jerseyNumber`, `firstName`,
 * `primaryPosition`, `secondaryPositions` and `ratings` in — which is precisely the criterion this
 * comment sets: what decides the number is whether a *published* field list changes. Read together
 * with `archive/import.ts`'s `OUR_PLAYER_RESHAPE_ARCHIVE_VERSION`. Unlike 2 and 3 this is **not** a
 * backfill: there is no honest transformation from a free-text blob to a jersey number, a position
 * and nineteen ratings, so the whole `ourPlayers` entity is **dropped at entity grain** from a
 * pre-format-4 archive and reported in its own `droppedLegacyEntities` field rather than as
 * fifteen quarantine lines in the archive-corruption vocabulary.
 */
export const ARCHIVE_FORMAT_VERSION = 4;

// ─────────────────────────────────────────────────────────────────────────────
// 7. Introduced by build unit U-7 — pre-match setup
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRE-4 / design §6.0 — the bounds of the shirt-number wheel.
 *
 * *"a **number wheel 1–99** for `shirtNumber`"*. The two numbers were already spelled at a call
 * site (`data/repos/teams.ts`'s `upsertPlayer` range guard) before this unit existed, which is
 * exactly the leak NFR-9 forbids: the wheel and the guard have to agree, and two literals in two
 * layers agree only until one of them is edited.
 *
 * ENV-11 is why the wheel is the whole identifier. *"The schema provides no field for a player's
 * name or physical appearance."* There is nothing else to pick.
 */
export const SHIRT_NUMBER_MIN = 1;

/** PRE-4 / design §6.0 — the top of the number wheel. See `SHIRT_NUMBER_MIN` for why both are here. */
export const SHIRT_NUMBER_MAX = 99;

// ─────────────────────────────────────────────────────────────────────────────
// 8. Introduced by build unit U-11 — the recorder, the Flag bar and voice notes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AUD-3 / design §8.5 — how long `finalizeRecording` waits for `MediaRecorder`'s `stop` event
 * before assembling what is already on disk.
 *
 * A **stall detector, not a budget.** The chunks are already persisted — §8.2 step 2 writes each
 * `dataavailable` in its own transaction *on arrival* — so the trailing `dataavailable` that `stop()`
 * may or may not emit is worth at most one `TIMESLICE_MS` of audio. AUD-3's actual requirement is
 * *"the recorder returns to idle"* and *"is never left in a state where a subsequent record attempt
 * fails"*, and both are violated by waiting forever for an event this platform is not obliged to
 * send. Three seconds is three timeslices: long enough that a merely slow stop is not truncated,
 * short enough that a wedged one cannot cost the user the next flag.
 *
 * Distinct from `COMMIT_EVENT_TIMEOUT_MS` on purpose. That one bounds an IndexedDB commit, whose
 * absence means a write did not happen; this one bounds a media event whose absence costs a second
 * of audio at most. Sharing a constant would tie two failures with nothing in common.
 */
export const RECORDER_STOP_TIMEOUT_MS = 3000;

// ─────────────────────────────────────────────────────────────────────────────
// 9. Introduced by build unit U-12 — the Set Piece sheet
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SP-2 / SP-3 — the touch target of a **placed marker**, the region a repeat tap must land in to
 * cycle its type.
 *
 * **Why 44 and not `MIN_TOUCH_TARGET_PT`'s 60.** NFR-3 asks for ≥ 60 pt in-play targets, and the
 * *placement* target here — the diagram itself — is an order of magnitude above that. The marker is
 * the secondary target, and at 60 pt it would make the primary action impossible: SP-2's own worked
 * case is a line of defenders across the six-yard box, and four 60 pt regions do not fit across a
 * six-yard box on a 390 pt-wide screen. A 60 pt marker would swallow the tap that was aimed at the
 * space beside it, so raising this number *reduces* what the user can capture.
 *
 * 44 is not a compromise invented here: §6.4 already sites a 44 pt in-play touch target inside a
 * ≥ 60 pt tile — `UNDO_INSET_PT`, LIVE-6's `−1` — so the design has already ruled that a secondary
 * region nested inside a large primary one is sized at 44. This is that ruling, one surface over.
 *
 * A separate constant from `UNDO_INSET_PT` rather than a reuse: two thresholds that happen to share a
 * value are not one threshold, and the day the undo inset moves, a marker must not move with it.
 *
 * Whether a thumb can reliably hit 44 pt among neighbouring markers is a **device** question — a
 * mouse hitting it in a WebKit pass proves nothing about a finger (ENV-1). VER-3 carries it.
 */
export const SET_PIECE_MARKER_PT = 44;

/**
 * SP-2 / SP-5 — the width : height ratio of the penalty-box diagram.
 *
 * **This is load-bearing for SP-5, not styling.** Markers are stored as normalized 0–1 box
 * coordinates so the share card can re-render them resolution-independently (CARD-1). That promise
 * holds only if `(0.5, 0.5)` means the same place on the pitch in every box it is rendered into — and
 * it does not, unless every box has the same aspect ratio. A diagram that reflowed to a different
 * ratio on rotation would silently move every marker relative to the six-yard box beneath it, with no
 * error anywhere and no way for the user to know the capture had changed.
 *
 * `1.6` frames the penalty area plus a strip of pitch in front of it: wide enough that the whole
 * 18-yard box and both posts are on screen, tall enough that markers at the edge of the box and
 * markers on the goal line are not compressed into the same few points.
 */
export const SET_PIECE_DIAGRAM_ASPECT = 1.6;

// ─────────────────────────────────────────────────────────────────────────────
// 10. Introduced by build unit U-15 — the share card
// ─────────────────────────────────────────────────────────────────────────────

/**
 * CARD-1 — the PNG's width in device-independent pixels.
 *
 * The card's **height is not a constant**: it grows with however many lines the three instructions
 * wrap to. A height cap would have to truncate a talk point, and a talk point the user authored and
 * then reads to a team is the one string in this product that must not be silently shortened
 * (INV-13 already refuses a blank one for the same reason). Height costs nothing in a message
 * bubble, which scales an image to the bubble's **width**, so a taller card renders at the same
 * type size — the legibility argument below is a width argument end to end.
 */
export const CARD_WIDTH_PX = 1080;

/**
 * CARD-1 — the type size of a talk point on the card, in the same pixels as `CARD_WIDTH_PX`.
 *
 * **This is the constant CARD-1's *"legible at phone-message size"* actually rests on**, and it is
 * meaningless on its own: what matters is its size *relative to the card's width*, because that
 * ratio is what survives a message client scaling the image into a bubble. `validate.ts` therefore
 * asserts the relationship rather than the number —
 * `CARD_TALK_POINT_PX × CARD_MESSAGE_WIDTH_PT ÷ CARD_WIDTH_PX ≥ CARD_MIN_LEGIBLE_PT` — so widening
 * the card without growing the type fails the build instead of quietly producing an unreadable
 * card that every automated check would still call green.
 */
export const CARD_TALK_POINT_PX = 56;

/**
 * CARD-1 — the message-bubble width, in points, that *"legible at phone-message size"* is stated
 * against.
 *
 * §0.3's rule applied to a layout claim: the assumption lives in a named value that a check can
 * read, not in a comment. iMessage on a 390 pt-wide iPhone renders an inbound image attachment at
 * roughly 250 pt across; that is the narrowest realistic delivery and therefore the one the floor
 * is stated against. If the real number turns out lower on the device, this constant moves and the
 * refinement in `validate.ts` reports immediately whether the type still clears the floor.
 */
export const CARD_MESSAGE_WIDTH_PT = 250;

/**
 * CARD-1 — the smallest rendered type size, in points, that counts as legible.
 *
 * 11 pt is iOS's own caption floor and is the size Apple's HIG treats as the lower bound for text
 * a user is expected to read rather than glance past. Below it the card stops being deliverable
 * from a phone, which is the whole of CARD-1's qualifier.
 *
 * **What this constant cannot settle.** Whether the card is legible *to a person* on a real phone
 * in real light is a device judgement (ENV-1), and it is a VER-3 obligation the browser pass prints
 * rather than discharges. This is the floor that makes the claim falsifiable, not evidence for it.
 */
export const CARD_MIN_LEGIBLE_PT = 11;

// ─────────────────────────────────────────────────────────────────────────────
// 11. Introduced by build unit U-20 — the app shell and OBS-1's remaining triggers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * OBS-1 / design §12.1a — the deferred flush: with entries buffered and no other trigger, flush
 * once after this many milliseconds of quiet.
 *
 * **Why the value.** §12.1a states the goal as bounding, *in time*, how much of the log a hard kill
 * can take: *"a kill loses at most five seconds of narrative while a quiet match half costs a
 * handful of writes"*. The failure this exists for is about **recency**, not volume — a quiet two
 * minutes producing three entries, followed by a jetsam kill, loses exactly the three entries that
 * describe the quiet period, and no count threshold above three ever reaches them. Five seconds is
 * small against the ninety-minute window OBS-1 has to explain and large against the ~1 Hz
 * `MediaRecorder` timeslice the design already accepts, so the wakeup rate stays bounded above by
 * the *logging* rate. It is a tuning constant; moving it is a config edit.
 *
 * **Why this is not the polling NFR-5 forbids.** The timer exists *only* while unflushed entries
 * exist, is limited to one outstanding deferral, and is cancelled by any other trigger — so an idle
 * app performs no logger work at all. §12.1a states the assertable form of that: *"no timer is
 * outstanding when the buffer is empty"*, which `logger.hasDeferredFlush()` exposes as a state a
 * test can read rather than a code-absence check that cannot tell a one-shot from an interval.
 *
 * **Why this is not a §1.3 row.** §12.1a says so explicitly: `spec13.test.ts`'s two-way diff runs
 * code → spec against section **1** only, and constants introduced by a build unit live under their
 * own banner — which is where `LOG_FLUSH_THRESHOLD` already sits, under U-0's.
 */
export const LOG_FLUSH_IDLE_MS = 5000;

// ─────────────────────────────────────────────────────────────────────────────
// 12. Introduced by build unit B-3 — the Coach Claude workflow (B spec §4.3)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * B spec §4.3 / §5.5 (scenario 28) — the hard client timeout on a `/v1/coach` generate call.
 *
 * The latency budget targets p50 ≤ 15 s and p95 ≤ 30 s for the round trip; this is the abort
 * bound past which the workflow stops waiting and hands the user straight into manual entry
 * (§5.4's `unreachable` arm). It exists because the flow is break-bounded: §5.5's arithmetic —
 * select + generate + review + card in 4–6 minutes of a 10–15 minute break — closes only if the
 * generate is *bounded*, not hoped fast. 45 s is three times the p50 target: far enough above it
 * that a slow-but-succeeding call is never cut off, small enough that the worst honest path still
 * leaves half the break to talk.
 */
export const COACH_TIMEOUT_MS = 45_000;

/**
 * B spec §4.3 / §2.6 — the timeout on the **Test connection** pre-flight (`GET /v1/coach/health`).
 *
 * The pre-flight exercises DNS, CORS, auth and Worker liveness without a model call, so it has
 * none of the generate's model latency and must answer much faster — a chip that spins for 45 s
 * is indistinguishable from the failure it exists to catch (pre-mortem incident 1: a config
 * failure worded as a network failure is how the flagship feature ships dead). 8 s covers a slow
 * cellular round trip with margin and is deliberately well under {@link COACH_TIMEOUT_MS}; the
 * relation is enforced in `validate.ts`.
 */
export const COACH_PREFLIGHT_TIMEOUT_MS = 8_000;

/**
 * B spec §4.3 / §5.3 / §20 — the most suggestions one generate response may contribute to a draft.
 *
 * **This is a structural ceiling, not an editorial one, and B-30 is where it stopped being both.**
 * Requirements §3.8.43 withdrew the editorial cap outright — *"i want to order from a bigger,
 * better menu"* — so the Worker no longer states any number to the model, in digits or in words,
 * and the argument this docstring used to make (*eight matches `BOARD_CAP`'s intuition — a
 * halftime talk that needs more than eight adjustments is not a halftime talk*) is exactly the
 * reasoning the stakeholder overruled. What remains is the one job §3.8.43 left standing: *"one
 * response may fill the card but not exceed it"*, which bounds a pathological answer and shapes
 * nothing.
 *
 * **Twenty, because it is `COACH_CARD_MAX_ENTRIES`.** `validate.ts`'s relation (suggestions ≤
 * entries) therefore holds **at equality** — the boundary `validate.test.ts` already certifies as
 * legal — and one response into an empty draft can fill the card exactly and never overflow it.
 * Truncation past this point is no longer silent: §20 has the review step say it once
 * (`truncationNoticeB`), because quietly trimming the menu is the failure §3.8.43 exists to end.
 *
 * There is deliberately **no Worker-side twin**. The service constant was deleted in B-30: it
 * existed only to be interpolated into the prompt, and a number that is not stated cannot drift.
 * `src/test/boundary.test.ts` now asserts that absence instead of an equality.
 */
export const COACH_MAX_SUGGESTIONS = 20;

/**
 * B spec §4.3 / §4.2 — the per-entry text bound `ERR_CARD_TOO_LARGE` enforces on a draft's
 * adjustments.
 *
 * **This bounds adjustments only.** `selectedHypothesisIds` is deliberately unbounded (scenario
 * 23 — zero to all, no cap; its absence is asserted in verification). The card renders one
 * adjustment per page in big arm's-length type (§2.5 step 4); 400 characters is several
 * sentences past the "≤2 sentences" the model is instructed to produce, so it guards pathology —
 * a paste, a runaway edit — never usage.
 */
export const COACH_ADJUSTMENT_MAX_CHARS = 400;

/**
 * B spec §4.3 / §4.2 — the entry-count bound `ERR_CARD_TOO_LARGE` enforces on a draft's
 * adjustments (the UI disables *Add* at this cap with a count note).
 *
 * Twenty pages is already an implausibly long halftime card; the cap keeps the `adjustments`
 * JSON column — read whole at interaction grain, the `setPieces.markers` shape — bounded by
 * construction: `COACH_ADJUSTMENT_MAX_CHARS × COACH_CARD_MAX_ENTRIES` is held under
 * {@link COACH_REQUEST_MAX_KB} in `validate.ts`, so a full card's text stays a few KB and every
 * whole-row commit stays a small transaction.
 */
export const COACH_CARD_MAX_ENTRIES = 20;

/**
 * B spec §4.3 / §5.1 — the request-size cap on a `/v1/coach` POST, kilobytes.
 *
 * Mirrored by a Worker-side constant held equal by a cross-package test (the `MAX_ARCHIVE_MB`
 * precedent) — that test lands with the Worker route in **B-17**, which reads this constant as
 * its client half. The cap cannot collide with scenario 23's no-cap selection: the library maxes
 * near ~49 patterns, and 49 selections with annotations plus a 20-player roster is a few KB
 * against 64 KB — the cap guards pathology, not usage (§4.3's own arithmetic).
 */
export const COACH_REQUEST_MAX_KB = 64;

// ─────────────────────────────────────────────────────────────────────────────
// 13. Introduced by build unit B-19a — the ruled lock-nudge threshold (LOCK-1)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * LOCK-1 / BAK-5 — the **inclusive lower bound** on a user-set `lockNudgeMin`, minutes.
 *
 * Not a round number picked for looks. LOCK-2's proposal *"is capped by the observed set"*
 * (design §6.6, the PRE-9 paragraph): a hypothesis with `cachedCount = 0` and no status override
 * is never offered, so at minute 0–4 of a half the "Lock my 8" offer would open a review screen
 * proposing approximately nothing. Below this the setting does not make the nudge earlier; it
 * makes it useless.
 *
 * These three constants are **not §1.3 rows** — §1.3 is the v1 inventory and carries
 * `LOCK_NUDGE_MIN` itself. Build-unit constants live under their own banner, which is the rule
 * `spec13.test.ts` enforces in both directions.
 */
export const LOCK_NUDGE_MIN_MIN = 5;

/**
 * LOCK-1 — the **inclusive upper bound** on a user-set `lockNudgeMin`, minutes. **Derived, not
 * chosen**, and the derivation is the whole reason the bound exists.
 *
 * At halftime the derived match minute *is* `halfLengthMin` (`domain/clock.deriveMatchMinute`'s
 * `halftime` arm), and `HALF_LENGTH_MIN_MIN` is the shortest half `Match.halfLengthMin` may hold
 * (SES-5). So a threshold at or below this number guarantees the offer is reachable **by the
 * break, on every half length the app permits** — which is when a board still has a second half
 * to serve, and when HT-3's hub expects it. A threshold above it would let a settings screen
 * silently push LOCK-1 — a MUST — past the only break the match has, on a legal configuration,
 * with nothing anywhere reporting that it had done so.
 *
 * `validate.ts` holds the relation rather than trusting this expression, so decoupling the two
 * fails the build instead of quietly widening the band.
 */
export const LOCK_NUDGE_MIN_MAX = HALF_LENGTH_MIN_MIN;

/**
 * LOCK-1 — the granularity of the offered `lockNudgeMin` choices, minutes.
 *
 * B's More tab derives its choice list from `[LOCK_NUDGE_MIN_MIN, LOCK_NUDGE_MIN_MAX]` at this
 * step rather than listing minutes, so the offered choices and the accepted band cannot disagree
 * — the failure `HARNESS_LIVE_TAP_MAX`'s note names ("two independent numbers that must agree
 * will eventually disagree"), which shipped once already in this repo. `validate.ts` additionally
 * requires `LOCK_NUDGE_MIN` itself to land on a step, or the default install would render a
 * control with nothing selected.
 *
 * Five minutes is the grain the question has: *"how far into a half before I have seen enough to
 * commit to a board"* is not a question anyone answers to the minute.
 */
export const LOCK_NUDGE_STEP_MIN = 5;
